import { checkPassword } from "api/authAPI";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { useAuth } from "components/Auth";
import Input from "components/atoms/input/Input";
import PasswordInput from "components/atoms/input/PasswordInput";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import NormalConfirmModal from "components/templates/modal/small/confirm/normal";
import useApiError from "hook/useApiError";
import useStatusMutation from "hook/useStatusMutation";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { COLORS } from "styles/colors";
import { FLEX } from "styles/flex";
import { calcRem } from "styles/theme";
import * as MediaQuery from "components/MediaQuery";

export type Props = {
  title: React.ReactNode | string;
  subTitle: string;
  onDeactive: () => void;
  disabled?: boolean;
  onCancel?: () => void;
  setIsShowModal: (isShowModal: boolean) => void;
};
export default function DeactivateModal({
  title,
  subTitle,
  onDeactive,
  disabled,
  onCancel,
  setIsShowModal,
}: Props): JSX.Element {
  const auth = useAuth();

  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const [password, setPassword] = useState<string>("");
  const [validateMsg, setValidateMsg] = useState<string>("");
  const [isValidated, setIsValidated] = useState<boolean>(false);
  const { displayError, intlString, lang } = useApiError();
  const defaultErrorMessage = intlString(
    lang.validateMsg.signup.loginIncorrect
  );

  const mutationCheckPassword = useStatusMutation(checkPassword, {
    onSuccess: (res: any) => {
      if (res.result.isExist) {
        setValidateMsg("");
        setIsValidated(false);
        setIsShowModal(false);
        onDeactive();
      } else {
        setIsValidated(true);
        setValidateMsg(defaultErrorMessage);
      }
    },
    onError: (err: CustomError) => {
      setIsValidated(true);
      setValidateMsg(displayError(err, defaultErrorMessage));
    },
  });
  const onClickCheckPassword = useCallback(() => {
    if (!mutationCheckPassword.idle) return;
    if (disabled) return;
    if (password === "") {
      setValidateMsg(intlString(lang.validateMsg.signup.password.empty));
      setIsValidated(true);
    } else {
      setValidateMsg("");
      setIsValidated(false);

      mutationCheckPassword.mutate({
        payload: {
          email: auth.user?.email,
          password: password,
        },
      });
    }
  }, [
    auth.user?.email,
    disabled,
    intlString,
    lang.validateMsg.signup.password.empty,
    mutationCheckPassword,
    password,
  ]);

  const onChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
    },
    []
  );

  const onConfirmCancel = useCallback(() => {
    setIsShowModal(false);
    onCancel?.();
  }, [onCancel]);

  useEffect(() => {
    return () => {
      setPassword("");
    };
  }, []);

  useEffect(() => {
    passwordInputRef.current?.focus();
  }, []);

  return (
    <NormalConfirmModal
      onConfirmCancel={onConfirmCancel}
      onConfirm={onClickCheckPassword}
      confirmLabel="OK"
      cancelLabel="Cancel"
      disabled={disabled}
      data-testid="blocks-modal-deactivate-modal"
    >
      <Container>
        <Title>
          {title}

          <br />
          <strong>{subTitle}</strong>
        </Title>
        <Inner>
          <ModalInput label="User ID">
            <Input disabled value={auth.user.email} />
          </ModalInput>
          <ModalInput
            label="Password"
            essential
            isValidated={isValidated}
            validatedWord={validateMsg}
          >
            <PasswordInput
              value={password}
              isValidated={isValidated}
              onChange={onChangeInput}
              onEnter={onClickCheckPassword}
              ref={passwordInputRef}
              data-testid="blocks-modal-deactivate-password-input-change-input"
            />
          </ModalInput>
        </Inner>
      </Container>
    </NormalConfirmModal>
  );
}

const Title = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.COLORS.WHITE};
  font-size: ${calcRem(20)};
  text-align: center;
  padding: 20px 0;
  line-height: ${calcRem(24)};
  word-break: break-word;
  white-space: pre-wrap;
  strong {
    color: ${COLORS.COMPANY};
    font-size: ${calcRem(16)};
    line-height: ${calcRem(20)};
  }
  ${MediaQuery.isDefault} {
    min-width: 400px;
  }
`;

const Container = styled.div`
  ${FLEX.FlexCenterCenter};
  flex-direction: column;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
`;
